import { navigateTabs } from "../lib/tabNavigation";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { IncidentCategory, IncidentFeature, IncidentSeverity } from "@chula/shared";
import { API_BASE } from "../lib/apiBase";
import { langOf } from "../lib/lang";

type RollTab = "active" | "archive";

interface CampusRow {
  id: string;
  ticket: string;
  createdAt: string;
  text: string;
  category: string;
  severity: string;
  status: "active" | "in_progress" | "fixed" | "archived";
  lat: number | null;
  lng: number | null;
  photoFile: string | null;
  sender: string;
  fixedAt: string | null;
  fixedBy: string | null;
  team?: string | null;
  rating?: number | null;
}

interface Props {
  /** Live open feed (Telegram + City Reporter) — already polled by App. */
  reports: IncidentFeature[];
  selectedId?: string | null;
  onSelect: (incident: IncidentFeature) => void;
  onFlyTo?: (lng: number, lat: number) => void;
  apiBase?: string;
}

function resolvePhotoUrl(url: string | undefined | null): string | null {
  if (!url) return null;
  // Absolute URLs (Telegram-concierge Google-Drive mirrors, etc) are already
  // publicly addressable. Local /api/reports/photos/... files are public on
  // the server too (the same Telegram photos are already public in the
  // reporting group), so both paths render without a token.
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
}

function ReportPhoto({ src }: { src: string | null }) {
  const [failedSource, setFailedSource] = useState<string | null>(null);
  return src && failedSource !== src
    ? <img className="issue-roll-thumb" src={src} alt="" loading="lazy" onError={() => setFailedSource(src)} />
    : <span className="issue-roll-thumb issue-roll-thumb--empty mono" title={src ? "Photo unavailable" : "No photo submitted"}>NO IMG</span>;
}

function fmtAgo(iso: string | undefined): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const min = Math.round(ms / 60_000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  if (min < 60 * 24) return `${Math.round(min / 60)}h`;
  return `${Math.round(min / (60 * 24))}d`;
}

function campusToFeature(r: CampusRow): IncidentFeature | null {
  if (r.lat === null || r.lng === null) return null;
  const closed = r.status === "fixed" || r.status === "archived";
  return {
    id: r.id,
    ticketNumber: r.ticket,
    lat: r.lat,
    lng: r.lng,
    category: (r.category as IncidentCategory) || "other",
    severity: (r.severity as IncidentSeverity) || "medium",
    status: closed ? "resolved" : r.status === "in_progress" ? "in-progress" : "received",
    title: r.text.slice(0, 80) || r.category,
    description: r.text,
    reportedAt: r.createdAt,
    resolvedAt: r.fixedAt ?? undefined,
    imageUrl: r.photoFile ? `/api/reports/photos/${r.photoFile}` : undefined,
    reporterPlatform: "internal",
    nickname: r.sender,
    staffName: r.fixedBy ?? (r.team ? "Assigned" : undefined),
    teamName: r.team ?? undefined,
    rating: r.rating != null ? String(r.rating) : undefined,
  };
}

const STATUS_DOT: Record<string, string> = {
  received: "var(--ink-low)",
  assigned: "var(--data)",
  "in-progress": "var(--warn)",
  resolved: "var(--good)",
};

/** Cards for Telegram campus reports use the accent token. */
const FRESH_WINDOW_MS = 5 * 60_000;

/**
 * Bottom-bar issue roll — replaces the idle traffic-hour slider.
 * ACTIVE = open map tickets; ARCHIVE = fixed / archived / resolved.
 * Click → parent opens IncidentCard + flies the map (City Reporter workflow).
 */
export const IssueRoll = memo(function IssueRoll({
  reports,
  selectedId,
  onSelect,
  onFlyTo,
  apiBase = API_BASE,
}: Props) {
  const [tab, setTab] = useState<RollTab>("active");
  const [closed, setClosed] = useState<IncidentFeature[]>([]);

  const loadClosed = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/api/incidents/campus-reports?status=closed`);
      if (!res.ok) return;
      const json = (await res.json()) as { reports?: CampusRow[] };
      const mapped = (json.reports ?? [])
        .map(campusToFeature)
        .filter((f): f is IncidentFeature => f !== null);
      // Also fold City Reporter tickets already marked resolved in the live feed.
      const fromFeed = reports.filter((r) => r.status === "resolved");
      const seen = new Set(mapped.map((m) => m.id));
      for (const r of fromFeed) {
        if (!seen.has(r.id)) mapped.push(r);
      }
      mapped.sort((a, b) => new Date(b.resolvedAt ?? b.reportedAt).getTime() - new Date(a.resolvedAt ?? a.reportedAt).getTime());
      setClosed(mapped.slice(0, 80));
    } catch {
      /* keep last good archive */
    }
  }, [apiBase, reports]);

  useEffect(() => {
    void loadClosed();
    const t = setInterval(() => { void loadClosed(); }, 30_000);
    return () => clearInterval(t);
  }, [loadClosed]);

  const active = useMemo(
    () =>
      [...reports]
        .filter((r) => r.status !== "resolved")
        .sort((a, b) => new Date(b.reportedAt).getTime() - new Date(a.reportedAt).getTime()),
    [reports],
  );

  const items = tab === "active" ? active : closed;

  const pick = (r: IncidentFeature) => {
    onSelect(r);
    if (Number.isFinite(r.lng) && Number.isFinite(r.lat)) onFlyTo?.(r.lng, r.lat);
  };

  return (
    <div className={`issue-roll${active.length > 0 ? " has-active" : ""}`} role="region" aria-label="Campus issue roll">
      <div className="issue-roll-head">
        <div className="issue-roll-title">
          <span className="eyebrow">Issues</span>
          <div className="issue-roll-tabs" role="tablist" tabIndex={-1} onKeyDown={navigateTabs}>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "active"}
              tabIndex={tab === "active" ? 0 : -1}
              className={`issue-roll-tab mono ${tab === "active" ? "is-active" : ""}${active.length > 0 ? " has-count" : ""}`}
              onClick={() => setTab("active")}
              title="Reports that still need attention"
            >
              OPEN · {active.length}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "archive"}
              tabIndex={tab === "archive" ? 0 : -1}
              className={`issue-roll-tab mono ${tab === "archive" ? "is-active" : ""}`}
              onClick={() => { setTab("archive"); void loadClosed(); }}
              title="Reports already fixed and archived"
            >
              DONE · {closed.length}
            </button>
          </div>
        </div>
        {items.length === 0 ? (
          // Empty is one line beside the tabs, not a second row: the bar has
          // nothing to list, so it should not spend a row saying so.
          <span
            className="issue-roll-empty mono caption"
            title={tab === "active"
              ? "No open reports. To report a problem, message @chuladigitaltwin_bot on Telegram."
              : "No fixed reports yet — fixed reports move here"}
          >
            {tab === "active"
              ? "No open reports — report via Telegram @chuladigitaltwin_bot"
              : "No fixed reports yet — fixed reports move here"}
          </span>
        ) : (
          <span
            className="issue-roll-hint mono caption"
            title="Click a report to see it on the map. Mark it fixed when done — the reporter gets a Telegram message."
          >
            Click a report to see it on the map. Mark it fixed when done — the reporter gets a Telegram message.
          </span>
        )}
      </div>

      {items.length === 0 ? null : (
        <ul className="issue-roll-track" aria-label={tab === "active" ? "Open reports" : "Fixed reports"}>
          {items.map((r) => {
            const src = resolvePhotoUrl(r.imageUrl);
            const selected = r.id === selectedId;
            const ticket = r.ticketNumber?.replace(/^CU-/, "") ?? r.id.slice(-6);
            const isInternal = r.reporterPlatform === "internal";
            const isFresh = isInternal &&
              r.status !== "resolved" &&
              Date.now() - new Date(r.reportedAt).getTime() < FRESH_WINDOW_MS;
            return (
              <li key={r.id} className="issue-roll-item">
              <button
                type="button"
                aria-pressed={selected}
                className={`issue-roll-card ${selected ? "is-selected" : ""} ${isInternal ? "is-internal" : ""} ${isFresh ? "is-fresh" : ""}`}
                onClick={() => pick(r)}
                title={r.description ?? r.title}
              >
                <span
                  className="issue-roll-dot"
                  style={{ background: STATUS_DOT[r.status] ?? "var(--ink-low)" }}
                  aria-hidden
                />
                <ReportPhoto src={src} />
                <span className="issue-roll-body">
                  <span className="issue-roll-ticket mono">
                    {ticket}
                  </span>
                  <span className="issue-roll-line" lang={langOf(r.title)}>{r.title}</span>
                  <span className="issue-roll-meta mono">
                    {r.status === "in-progress" ? "FIXING" : r.status === "resolved" ? "DONE" : "NEW"}
                    {" · "}
                    {fmtAgo(r.reportedAt)}
                    {r.severity === "high" || r.severity === "critical" ? " · HIGH" : ""}
                  </span>
                </span>
              </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});
